import api from './api';

export const NEAR_ME_REFRESH_MS = 6 * 60 * 60 * 1000;
const POSITION_CACHE_MS = 30 * 60 * 1000;

export function isNearMeMode(preferences) {
  return preferences?.locationPreferences?.useCurrentLocation !== false;
}

export function nearMeIsFresh(eventLocation) {
  const lastUpdated = eventLocation?.lastUpdated;
  if (!lastUpdated) return false;
  const age = Date.now() - new Date(lastUpdated).getTime();
  return Number.isFinite(age) && age >= 0 && age < NEAR_ME_REFRESH_MS;
}

function roundCoord(value) {
  return Math.round(Number(value) * 100) / 100;
}

export function requestBrowserLocation({ maximumAge = POSITION_CACHE_MS } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error('Geolocation unavailable');
      err.code = 'unavailable';
      reject(err);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: roundCoord(pos.coords.latitude),
          lng: roundCoord(pos.coords.longitude),
        });
      },
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 8000, maximumAge }
    );
  });
}

/**
 * Refresh the cached city/country used for event discovery.
 * Does not store precise coordinates. Reuses a granted browser permission
 * via maximumAge rather than prompting on every visit.
 */
export async function refreshNearMeIfNeeded({ preferences, eventLocation, force = false } = {}) {
  if (!isNearMeMode(preferences)) return { status: 'skipped' };
  if (!force && nearMeIsFresh(eventLocation)) return { status: 'fresh' };

  try {
    const coords = await requestBrowserLocation({
      maximumAge: force ? 60000 : POSITION_CACHE_MS,
    });
    const res = await api.post('/api/user/resolve-event-location', coords);
    return {
      status: 'updated',
      eventLocation: res.data?.eventLocation || null,
    };
  } catch (err) {
    if (err && err.code === 1) return { status: 'denied', eventLocation: eventLocation || null };
    if (err && (err.code === 2 || err.code === 3 || err.code === 'unavailable')) {
      return { status: 'unavailable', eventLocation: eventLocation || null };
    }
    return { status: 'error', eventLocation: eventLocation || null };
  }
}
